import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { submitLead, type LeadFormData } from "@/lib/leadService";
import { trackFormSubmission, trackConversion, trackContactSubmit, trackCTAClick } from "@/lib/analytics";
import { bookingHref, bookingTarget } from "@/lib/booking";

interface ContactCTAProps { pageSource?: string; selectedPath?: string; }
type FieldName = "fullName" | "company" | "email" | "message";

const ContactCTA = ({ pageSource = "Home", selectedPath }: ContactCTAProps) => {
  const reduced = useReducedMotion();
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [formData, setFormData] = useState({ fullName: "", company: "", email: "", message: "", agreed: false });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = (name: FieldName, value: string) => {
    if (!value.trim()) return name === "email" ? "Please enter your work email" : `Please enter your ${name === "fullName" ? "full name" : name}`;
    if (name === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Please enter a valid work email, e.g. jane@company.com";
    if (name === "message" && value.length > 1000) return "Message must be less than 1000 characters";
    return "";
  };
  const handleBlur = (name: FieldName) => {
    setTouched((value) => ({ ...value, [name]: true }));
    setErrors((value) => ({ ...value, [name]: validateField(name, formData[name]) }));
  };
  const fieldError = (name: FieldName) => touched[name] ? errors[name] : "";
  const inputAria = (name: FieldName) => ({ "aria-invalid": Boolean(fieldError(name)), "aria-describedby": fieldError(name) ? `${name}-error` : undefined });

  const validateForm = () => {
    const next = Object.fromEntries((["fullName", "company", "email", "message"] as FieldName[]).map((name) => [name, validateField(name, formData[name])]).filter(([, message]) => message));
    if (!formData.agreed) next.agreed = "You must agree to the terms";
    setTouched({ fullName: true, company: true, email: true, message: true });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    const leadData: LeadFormData = { fullName: formData.fullName, companyName: formData.company, email: formData.email, phone: "", country: "", industry: "", message: formData.message, pageSource, selectedPath: selectedPath || null };
    const result = await submitLead(leadData, honeypot);
    setIsSubmitting(false);
    if (result.success) {
      trackFormSubmission("contact_form", true);
      trackConversion("schedule_a_call");
      trackContactSubmit(pageSource, "");
      setIsSubmitted(true);
      toast({ title: "Thanks.", description: "A senior partner replies within one business day." });
    } else {
      trackFormSubmission("contact_form", false);
      toast({ title: "Error", description: result.error || "Something went wrong. Please try again.", variant: "destructive" });
    }
  };

  if (isSubmitted) return <section id="contact" className="section-tint py-16"><div className="container mx-auto"><div className="surface-card mx-auto max-w-lg p-12 text-center"><CheckCircle2 className="mx-auto mb-6 h-12 w-12 text-good" /><h3 className="mb-4">Thank you.</h3><p className="text-ink-2">A senior partner replies within one business day.</p></div></div></section>;

  return (
    <section id="contact" className="section-tint relative overflow-hidden py-16 md:py-24">
      <div className="section-sheen" aria-hidden="true" />
      <div className="container mx-auto relative">
        <div className="grid gap-10 lg:grid-cols-[.9fr_1.1fr] lg:items-center">
          <motion.div initial={reduced ? false : { opacity: .1, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: .55, ease: [.2, .7, .2, 1] }}>
             <p className="eyebrow mb-4">Start here</p>
             <h2 className="mb-6">We win when you win.</h2>
             <p className="lede max-w-[60ch]">A 30-minute diagnostic call with a senior partner. No scope, no pitch. You leave with a clear read on where the constraint is, whether or not you work with us.</p>
             <Button asChild variant="primary" size="lg" className="mt-8">
               <a href={bookingHref} target={bookingTarget} rel={bookingTarget ? "noopener noreferrer" : undefined} onClick={() => trackCTAClick("Book diagnostic call", "contact_intro")}>Book on the calendar →</a>
             </Button>
             <p className="mt-4 text-sm text-ink-3">Replies within one business day. Confidential by default.</p>
          </motion.div>
           <motion.div initial={reduced ? false : { opacity: .1, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: .55, delay: .07, ease: [.2, .7, .2, 1] }}>
             <div className="gradient-frame">
              <form onSubmit={handleSubmit} className="surface-card p-6 md:p-8" noValidate>
               <h3 className="mb-6">Tell us where the flow breaks</h3>
              <div className="absolute -left-[9999px]" aria-hidden="true"><Input type="text" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(event) => setHoneypot(event.target.value)} /></div>
              <div className="space-y-5">
                {(["fullName", "email", "company"] as FieldName[]).map((name) => {
                  const labels = { fullName: "Full Name", email: "Work Email", company: "Company", message: "Message" };
                  return <div key={name} className="space-y-2"><Label htmlFor={name}>{labels[name]} *</Label><Input id={name} name={name} type={name === "email" ? "email" : "text"} required value={formData[name]} onChange={(event) => setFormData({ ...formData, [name]: event.target.value })} onBlur={() => handleBlur(name)} className={fieldError(name) ? "border-destructive" : ""} {...inputAria(name)} />{fieldError(name) && <p id={`${name}-error`} role="alert" className="text-destructive text-sm">{fieldError(name)}</p>}</div>;
                })}
                 <div className="space-y-2"><Label htmlFor="message">What is the problem, in a sentence or two? *</Label><Textarea id="message" name="message" required maxLength={1000} className={`min-h-[120px] ${fieldError("message") ? "border-destructive" : ""}`} value={formData.message} onChange={(event) => setFormData({ ...formData, message: event.target.value })} onBlur={() => handleBlur("message")} {...inputAria("message")} />{fieldError("message") && <p id="message-error" role="alert" className="text-destructive text-sm">{fieldError("message")}</p>}</div>
                <div className="flex items-start gap-3"><Checkbox id="agreed" checked={formData.agreed} onCheckedChange={(checked) => setFormData({ ...formData, agreed: checked === true })} required /><Label htmlFor="agreed" className="text-sm text-muted-foreground font-normal leading-relaxed">I agree to the <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a> and <a href="/terms" className="text-primary hover:underline">Terms of Service</a></Label></div>
                {errors.agreed && <p role="alert" className="text-destructive text-sm">{errors.agreed}</p>}
                 <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isSubmitting}>{isSubmitting ? "Sending…" : "Request the call →"}</Button>
              </div>
            </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
export default ContactCTA;
